import {
  Client,
  ConnectionState,
  Conversation,
  Message,
} from "@twilio/conversations";
import { useCallback, useEffect, useRef, useState } from "react";
import useRecordLongLoading from "@/hooks/messages/useRecordLongLoading";
import { ClientState } from "@/types/messages";
import {
  getSubscribedConversations,
  isErrorConnectionState,
  sortConversations,
} from "@/utils/messages";
import useErrorLogger from "@/utils/useErrorLogger";

const defaultClientState: ClientState = {
  state: "loading",
  isLongLoading: false,
  connectionState: "connecting",
};

// TWILIO OFFICIAL EXAMPLE:
// https://github.com/twilio/twilio-conversations-demo-react/blob/af30e30f4183c9c004b89749202b669cd67b2942/src/components/AppContainer.tsx
export const useTwilioClient = ({
  getToken,
}: {
  getToken: () => Promise<string>;
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [lastMessageSid, setLastMessageSid] = useState("");
  const [clientState, setClientState] =
    useState<ClientState>(defaultClientState);

  const [client, setClient] = useState<Client>(null);

  // Used to re-create the client when token is expired
  const [clientIteration, setClientIteration] = useState(0);

  const logError = useErrorLogger();

  // temporary solution
  const attempt = useRef(0);

  const { registerStartLoading, registerFinishLoading } = useRecordLongLoading(
    useCallback(
      () =>
        setClientState(({ connectionState }) => ({
          connectionState,
          state: "loading",
          isLongLoading: true,
        })),
      []
    )
  );

  useEffect(() => {
    let client: Client;
    const initClient = async () => {
      const token = await getToken();
      if (!token) return;

      client = new Client(token);
      setClient(client);
      registerStartLoading();

      client.on(
        "connectionStateChanged",
        (connectionState: ConnectionState) => {
          if (isErrorConnectionState(connectionState)) {
            setClientState({
              state: "error",
              connectionState,
            });
            logError(
              new Error(`Twilio client connection state is ${connectionState}`),
              null,
              { attempt: attempt.current }
            );
            attempt.current++;

            return;
          }
          setClientState({
            state: "ready",
            connectionState,
          });
          attempt.current = 0;
        }
      );

      client.on("connectionError", (event) => {
        if (event.terminal) {
          setClientState({
            connectionState: client.connectionState,
            state: "error",
            customErrorMessage: event.message,
          });
          logError("Twilio connection error", event.message, {
            event,
            attempt: attempt.current,
          });
          attempt.current++;
        }
      });

      client.on("initFailed", (event) => {
        registerFinishLoading();
        logError("Twilio client initFailed", null, {
          error: event.error,
          attempt: attempt.current,
        });
        attempt.current++;
        setClientState({
          state: "error",
          initFailedError: event.error,
          connectionState: client.connectionState,
        });
      });

      client.on("initialized", async () => {
        try {
          const conversations = await getSubscribedConversations(client);
          sortConversations(conversations);
          setConversations(conversations);
          setClientState({
            state: "ready",
            connectionState: client.connectionState,
          });
          attempt.current = 0;
        } catch (error) {
          logError(error, "getSubscribedConversations() failed", {
            attempt: attempt.current,
          });
          setClientState({
            state: "error",
            connectionState: client.connectionState,
            customErrorMessage: "Could not load conversations.",
          });
          attempt.current++;
        } finally {
          registerFinishLoading();
        }
      });

      client.on("conversationJoined", (conversation) => {
        setConversations((prev) => [...prev, conversation] as Conversation[]);
      });

      client.on("conversationLeft", (leftConversation) => {
        setConversations((prev) =>
          prev.filter((conv) => conv !== leftConversation)
        );
      });

      client.on("messageAdded", async (message: Message) => {
        try {
          const conversations = await getSubscribedConversations(client);
          if (conversations.length) {
            sortConversations(conversations);
            setConversations(conversations);
            setLastMessageSid(message.sid);
          }
        } catch (error) {
          logError(
            error,
            'on "messageAdded" getSubscribedConversations() failed'
          );
        }
      });

      client.on("tokenAboutToExpire", async () => {
        const token = await getToken();
        await client.updateToken(token);
      });

      client.on("tokenExpired", () => {
        setClientIteration((x) => x + 1);
      });
    };

    initClient();

    return () => {
      client?.removeAllListeners();
    };
  }, [
    getToken,
    logError,
    registerFinishLoading,
    registerStartLoading,
    clientIteration,
  ]);

  const shutdown = useCallback(() => {
    if (client) {
      client.removeAllListeners();
      client.shutdown();

      setConversations([]);
      setLastMessageSid("");
      setClientState(defaultClientState);
      setClient(null);
    }
  }, [client]);

  return {
    client,
    conversations,
    lastMessageSid,
    clientState,
    shutdown,
  };
};
